import { useState, useEffect, useRef } from 'react';
import { LuCircleCheck, LuFileText, LuUpload, LuX } from 'react-icons/lu';
import Drawer from '../Drawer/Drawer';
import { updateOrder, uploadFile, createPayment, fetchPaymentForOrder, fetchMyPartner, getAssetUrl, notifyAdmins, sendNotification, fetchOrderTrail } from '../../lib/directus';
import { buildFallbackTrail, normalizeOrderTrail } from '../../lib/orderTrail';
import styles from '../TraderOrderDrawer/TraderOrderDrawer.module.css';

export default function BuyerOrderDrawer({ order, onClose, readOnly = false, onUpdated }) {
  const [file, setFile]           = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [payment, setPayment]     = useState(null);
  const [trail, setTrail]         = useState([]);
  const fileRef                   = useRef(null);

  useEffect(() => {
    if (order) {
      setFile(null);
      setConfirmed(false);
      setError('');
      setPayment(null);
      setTrail([]);
      if (order.escrow_status !== 'pending_approval') {
        fetchPaymentForOrder(order.id).then(setPayment).catch(() => {});
      }
      fetchOrderTrail({ orderId: order.id })
        .then(data => setTrail(normalizeOrderTrail(data)))
        .catch(() => setTrail([]));
    }
  }, [order?.id]);

  if (!order) return null;

  const stockValue  = parseFloat(order.stockValue?.replace(/[^0-9.]/g, '')) || 0;
  const transport   = 1000;
  const platformFee = parseFloat((stockValue * 0.01).toFixed(2));
  const total       = (stockValue + transport + platformFee).toFixed(2);
  const proofFile = payment?.proof_document;
  const proofUrl  = proofFile ? getAssetUrl(typeof proofFile === 'object' ? proofFile.id : proofFile) : null;
  const timelineItems = trail.length > 0
    ? trail
    : buildFallbackTrail(order.fulfillment_status || order.escrow_status, order.date_created, 'order');

  function renderTimeline() {
    return (
      <>
        <p className={styles.timelineTitle}>Progress timeline</p>
        <div className={styles.card}>
          <div className={styles.timeline}>
            {timelineItems.map((step, idx) => {
              const completed = step.state === 'done';
              const active = step.state === 'active';
              return (
                <div key={step.key} className={styles.timelineItem}>
                  <div className={styles.timelineLeft}>
                    <div className={`${styles.timelineDot} ${completed ? styles.dotDone : active ? styles.dotActive : styles.dotPending}`}>
                      {completed && <LuCircleCheck size={16} />}
                    </div>
                    {idx < timelineItems.length - 1 && (
                      <div className={`${styles.timelineLine} ${completed ? styles.lineDone : styles.linePending}`} />
                    )}
                  </div>
                  <div className={styles.timelineContent}>
                    <p className={`${styles.stepLabel} ${completed || active ? styles.stepLabelDone : styles.stepLabelPending}`}>
                      {step.label}
                    </p>
                    {!!step.meta && <p className={styles.stepMeta}>{step.meta}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  function handleClose() {
    setFile(null);
    setConfirmed(false);
    setError('');
    onClose();
  }

  async function handleSubmitProof() {
    setLoading(true);
    setError('');
    try {
      let proofId = null;
      if (file) {
        proofId = await uploadFile(file);
      }
      const partner = await fetchMyPartner();
      await createPayment({
        order:   order.id,
        amount:  parseFloat(total).toFixed(2),
        ...(partner ? { partner: partner.id } : {}),
        ...(proofId ? { proof_document: proofId } : {}),
      });
      notifyAdmins(
        'Proof of payment submitted',
        `Buyer ${order.buyer || ''} has submitted proof of payment for order ${order.orderId} (${order.commodity}${order.grade ? ', ' + order.grade : ''}). Total: R${parseFloat(total).toLocaleString()}. Please review and approve.`
      ).catch(console.error);
      onUpdated && onUpdated();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdminApprove() {
    setLoading(true);
    setError('');
    try {
      await updateOrder(order.id, { escrow_status: 'escrow_locked' });
      if (order.buyerUserId) {
        sendNotification(
          order.buyerUserId,
          'Payment approved — escrow locked',
          `Your payment for order ${order.orderId} (${order.commodity}${order.grade ? ', ' + order.grade : ''}) has been approved and funds are now locked in escrow. Your order is being processed.`
        ).catch(console.error);
      }
      onUpdated && onUpdated();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdminReject() {
    setLoading(true);
    setError('');
    try {
      await updateOrder(order.id, { escrow_status: 'awaiting_finance' });
      onUpdated && onUpdated();
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Awaiting trader approval ──────────────────────────────────
  if (order.escrow_status === 'pending_approval') {
    return (
      <Drawer isOpen={!!order} title={order.orderId} onClose={handleClose}>
        <div className={styles.card}>
          <div className={styles.threeCol}>
            <div>
              <p className={styles.fieldLabel}>Commodity</p>
              <p className={styles.fieldValue}>{order.commodity}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Grade</p>
              <p className={styles.fieldValue}>{order.grade}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Quantity</p>
              <p className={styles.fieldValue}>{order.quantity}</p>
            </div>
          </div>
          {order.deliveryLocation && (
            <div style={{ marginTop: 16 }}>
              <p className={styles.fieldLabel}>Delivery to</p>
              <p className={styles.fieldValue}>{order.deliveryLocation}</p>
            </div>
          )}
        </div>
        <div className={styles.card}>
          <p className={styles.cardTitle}>Order summary</p>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Stock value</span>
            <span className={styles.summaryVal}>R{stockValue.toLocaleString()}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Estimated transport</span>
            <span className={styles.summaryVal}>R{transport.toLocaleString()}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Platform Fee (1%)</span>
            <span className={styles.summaryVal}>R{platformFee.toLocaleString()}</span>
          </div>
          <div className={styles.divider} />
          <div className={styles.summaryRow}>
            <span className={styles.totalLabel}>Total estimated</span>
            <span className={styles.totalVal}>R{parseFloat(total).toLocaleString()}</span>
          </div>
        </div>
        <div className={styles.card} style={{ textAlign: 'center', color: 'var(--secondary-text)', fontSize: 13, fontFamily: '"General Sans", sans-serif' }}>
          Awaiting trader approval
        </div>
      </Drawer>
    );
  }

  // ── Awaiting finance — Admin view ─────────────────────────────
  if (order.escrow_status === 'awaiting_finance' && readOnly) {
    const adminFooter = proofUrl ? (
      <>
        <button className={styles.btnReject} onClick={handleAdminReject} disabled={loading}>
          Reject payment
        </button>
        <button className={styles.btnApprove} onClick={handleAdminApprove} disabled={loading}>
          {loading ? 'Approving...' : 'Approve payment'}
        </button>
      </>
    ) : null;

    return (
      <Drawer isOpen={!!order} title={order.orderId} onClose={handleClose} footer={adminFooter}>
        <div className={styles.card}>
          <div className={styles.threeCol}>
            <div>
              <p className={styles.fieldLabel}>Commodity</p>
              <p className={styles.fieldValue}>{order.commodity}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Grade</p>
              <p className={styles.fieldValue}>{order.grade}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Quantity</p>
              <p className={styles.fieldValue}>{order.quantity}</p>
            </div>
          </div>
          <div className={styles.twoCol} style={{ marginTop: 16 }}>
            <div>
              <p className={styles.fieldLabel}>Buyer</p>
              <p className={styles.fieldValue}>{order.buyer}</p>
            </div>
            <div>
              <p className={styles.fieldLabel}>Amount</p>
              <p className={styles.escrowAmount}>R{parseFloat(total).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {proofUrl ? (
          <>
            <p className={styles.timelineTitle}>Proof of payment</p>
            <div className={styles.card}>
              <a href={proofUrl} target="_blank" rel="noopener noreferrer" className={styles.proofLink}>
                <LuFileText size={16} color="var(--olive-color)" />
                <span>View proof of payment</span>
              </a>
            </div>
          </>
        ) : (
          <div className={styles.card} style={{ textAlign: 'center', color: 'var(--secondary-text)', fontSize: 13, fontFamily: '"General Sans", sans-serif' }}>
            Awaiting buyer proof of payment
          </div>
        )}

        {renderTimeline()}
        {error && <p className={styles.error}>{error}</p>}
      </Drawer>
    );
  }

  // ── Awaiting finance — Buyer uploads proof ────────────────────
  if (order.escrow_status === 'awaiting_finance' && !readOnly) {
    if (payment) {
      return (
        <Drawer isOpen={!!order} title={order.orderId} onClose={handleClose}>
          <div className={styles.card}>
            <div className={styles.threeCol}>
              <div>
                <p className={styles.fieldLabel}>Commodity</p>
                <p className={styles.fieldValue}>{order.commodity}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Grade</p>
                <p className={styles.fieldValue}>{order.grade}</p>
              </div>
              <div>
                <p className={styles.fieldLabel}>Quantity</p>
                <p className={styles.fieldValue}>{order.quantity}</p>
              </div>
            </div>
          </div>
          <div className={styles.card} style={{ textAlign: 'center', color: 'var(--secondary-text)', fontSize: 13, fontFamily: '"General Sans", sans-serif' }}>
            Proof of payment submitted. Awaiting admin review.
          </div>
        </Drawer>
      );
    }

    const canSubmit = !!file && confirmed;
    const footer = (
      <button
        className={`${styles.btnLock} ${!canSubmit ? styles.btnLockDisabled : ''}`}
        disabled={!canSubmit || loading}
        onClick={handleSubmitProof}
      >
        {loading ? 'Submitting...' : 'Submit proof of payment'}
      </button>
    );

    return (
      <Drawer isOpen={!!order} title={order.orderId} onClose={handleClose} footer={footer}>
        <div className={styles.card}>
          <p className={styles.cardTitle}>Upload proof of payment</p>
          {file ? (
            <div className={styles.fileUploaded}>
              <LuFileText size={16} color="var(--olive-color)" />
              <div className={styles.fileInfo}>
                <p className={styles.fileName}>{file.name}</p>
                <p className={styles.fileMeta}>{(file.size / (1024 * 1024)).toFixed(1)} MB • Ready to upload</p>
              </div>
              <button className={styles.removeFile} onClick={() => { setFile(null); setConfirmed(false); }}>
                <LuX size={14} />
              </button>
            </div>
          ) : (
            <button className={styles.uploadArea} onClick={() => fileRef.current?.click()}>
              <LuUpload size={14} color="var(--olive-color)" />
              <span>Tap to upload file (PDF, JPG, PNG, 10 MB max.)</span>
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => e.target.files[0] && setFile(e.target.files[0])}
          />
        </div>

        <div className={styles.card}>
          <p className={styles.cardTitle}>Order summary</p>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Order#</span>
            <span className={styles.summaryVal}>{order.orderId}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Amount due</span>
            <span className={styles.totalVal}>R{parseFloat(total).toLocaleString()}</span>
          </div>
        </div>

        {file && (
          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={e => setConfirmed(e.target.checked)}
              className={styles.checkbox}
            />
            <span className={styles.checkLabel}>
              I confirm payment of R{parseFloat(total).toLocaleString()} has been transferred to the escrow account
            </span>
          </label>
        )}

        {error && <p className={styles.error}>{error}</p>}
      </Drawer>
    );
  }

  // ── Timeline view (finance_approved and beyond) ───────────────
  return (
    <Drawer isOpen={!!order} title="View details" onClose={handleClose}>
      <div className={styles.card}>
        <div className={styles.threeCol}>
          <div>
            <p className={styles.fieldLabel}>Commodity</p>
            <p className={styles.fieldValue}>{order.commodity}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Grade</p>
            <p className={styles.fieldValue}>{order.grade}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Quantity</p>
            <p className={styles.fieldValue}>{order.quantity}</p>
          </div>
        </div>
        <div className={styles.twoCol} style={{ marginTop: 16 }}>
          <div>
            <p className={styles.fieldLabel}>Seller</p>
            <p className={styles.fieldValue}>{order.seller}</p>
          </div>
          <div>
            <p className={styles.fieldLabel}>Escrow amount</p>
            <p className={styles.escrowAmount}>R{parseFloat(total).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <p className={styles.timelineTitle}>Progress timeline</p>
      <div className={styles.card}>
        <div className={styles.timeline}>
          {timelineItems.map((step, idx) => {
            const completed = step.state === 'done';
            const active = step.state === 'active';
            return (
              <div key={step.key} className={styles.timelineItem}>
                <div className={styles.timelineLeft}>
                  <div className={`${styles.timelineDot} ${completed ? styles.dotDone : active ? styles.dotActive : styles.dotPending}`}>
                    {completed && <LuCircleCheck size={16} />}
                  </div>
                  {idx < timelineItems.length - 1 && (
                    <div className={`${styles.timelineLine} ${completed ? styles.lineDone : styles.linePending}`} />
                  )}
                </div>
                <div className={styles.timelineContent}>
                  <p className={`${styles.stepLabel} ${completed || active ? styles.stepLabelDone : styles.stepLabelPending}`}>
                    {step.label}
                  </p>
                  {!!step.meta && <p className={styles.stepMeta}>{step.meta}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {proofUrl && (
        <>
          <p className={styles.timelineTitle}>Proof of payment</p>
          <div className={styles.card}>
            <a href={proofUrl} target="_blank" rel="noopener noreferrer" className={styles.proofLink}>
              <LuFileText size={16} color="var(--olive-color)" />
              <span>View proof of payment</span>
            </a>
          </div>
        </>
      )}
    </Drawer>
  );
}
